/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
// import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Button } from '@mui/material';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import './timesheets.css';
import ExportAllModal from './ExportAllModal';
import Pagination from '../../components/pagination';
import { Navbar, Sidebar } from '../../components';
import useDebounce from '../../hooks/useDebounce';
import MemberNameEmailColumn from '../../components/table/MemberNameEmailColumn';
import { formatActivityPercentage } from '../../utils/activity';
import { formatSeconds } from '../../utils/time';
import CustomTable from '../../components/table/CustomTable';
import DateRangePicker from '../../components/dateRangePicker';
import TeamSelect from '../userDashboard/TeamSelect';
import TimezoneSelectWrapper from '../../components/selectors/TimezoneSelectWrapper';

const TABLE_HEADERS = [
  {
    label: 'User',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'name',
    colSpan: 1.5,
  },
  {
    label: 'Total Time',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'totalTime',
  },
  {
    label: 'Activity Level',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'activityLevel',
  },
  {
    label: '',
    colSpan: 0.5,
  },
];

const CustomTableStyles = {
  container: {
    marginTop: 1,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '16px',
    '.MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg) !important',
    },
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    button: {
      textWrap: 'nowrap',
      color: 'var(--dark-2)',
      borderColor: 'var(--dark-4) !important',
    },
  },
};

function Timesheets() {
  // const today = new Date();
  const { search } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const { activeWorkspace } = useContext(UserContext);
  const [members, setMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCounts, setTotalCounts] = useState(0);
  const [showExportAllModal, setShowExportAllModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { debounce } = useDebounce();
  const [sort, setSort] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(0);
  const [selectedTimezone, setSelectedTimezone] = useState(activeWorkspace.timezone);
  const [date, setDate] = useState(() => {
    const initialToday = moment.tz(selectedTimezone).toDate();
    return [initialToday, initialToday];
  });

  useEffect(() => {
    const initialToday = moment.tz(selectedTimezone).startOf('day').toDate();
    setDate([initialToday, initialToday]);
  }, [selectedTimezone]);

  const handleSelectDate = async (timezone = null) => {
    const [startDate, endDate] = date;
    const tz = timezone || selectedTimezone;

    // const dates = getDatesInRange(startDate, endDate);

    const startOfDay = moment.tz(startDate, tz)
      .startOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const endOfDay = moment.tz(endDate, tz)
      .endOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const baseParams = {
      start: startOfDay,
      end: endOfDay,
      page: currentPage,
      name: searchQuery,
      teamId: selectedTeamId,
      sortKey,
      sortOrder,
      pageSize,
      timezone: tz,
    };

    setIsLoading(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.membersActivity(activeWorkspace.id),
      timeout: 60000,
      params: {
        ...baseParams,
      },
    });
    if (response.success) {
      const { mappedOffTime, totalOffTime } = response.data.offTimeRequests?.reduce(
        (acc, entry) => {
          const seconds = Number(entry.totalHours || 0) * 60;
          const { memberId, date: entryDate } = entry;

          // Check if entryDate is within the selected date range (inclusive)
          const [startDate, endDate] = date;
          const entryMoment = moment(entryDate);
          if (
            entryMoment.isSameOrAfter(moment(startDate), 'day')
            && entryMoment.isSameOrBefore(moment(endDate), 'day')
          ) {
            if (!acc.mappedOffTime[memberId]) acc.mappedOffTime[memberId] = 0;
            acc.mappedOffTime[memberId] += seconds;
            acc.totalOffTime += seconds;
          }
          return acc;
        },
        { mappedOffTime: {}, totalOffTime: 0 },
      ) || { mappedOffTime: {}, totalOffTime: 0 };

      const { mappedManualTimeEntries, totalManualTime } = response.data.manualEntries?.reduce(
        (acc, entry) => {
          const seconds = Number(entry.secondsElapsed) || 0;
          acc.mappedManualTimeEntries[entry.memberId] = seconds;
          acc.totalManualTime += seconds;
          return acc;
        },
        { mappedManualTimeEntries: {}, totalManualTime: 0 },
      ) || { mappedManualTimeEntries: {}, totalManualTime: 0 };

      const mappedMembers = response.data?.members.map((member) => {
        const { activityPercentage, secondsElapsed } = member;
        const manualTimeEntrySeconds = mappedManualTimeEntries[member.id] || 0;
        const ptoSeconds = mappedOffTime[member.id] || 0;
        const totalSeconds = (secondsElapsed ?? 0) + manualTimeEntrySeconds + ptoSeconds;
        return ({
          name: <MemberNameEmailColumn
            profilePicture={member?.user?.profilePicture}
            firstName={member.haveJoined ? member?.user?.firstName : ''}
            lastName={member.haveJoined ? member?.user?.lastName : ''}
            email={member?.user?.email}
            id={member?.id}
            component="div"
          />,

          totalTime: formatSeconds(totalSeconds),
          activityLabel: `${activityPercentage ? parseFloat(activityPercentage * 100).toFixed(0) : '--'}%`,
          actions: (
            <Link to={{
              pathname: '/members/activity/view',
              search: `?member=${member.id}`,
              state: {
                workspaceMember: member,
                previouslySelectedDate: date[0],
              },
            }}
            >
              <Button
                variant="outlined"
                style={{ textWrap: 'no-wrap' }}
              >
                View Activity
              </Button>
            </Link>
          ),
        });
      });

      const totalRow = {
        name: <p className="fw-medium fc-medium" style={{ padding: '8px 0px', display: 'block' }}> Total </p>,
        totalTime: formatSeconds((response.data?.totalSecondsElapsed ?? 0) + totalManualTime + totalOffTime),
        activityLabel: `${formatActivityPercentage(
          response.data?.totalActiveSeconds,
          response.data?.totalSecondsElapsed,
        ) ?? '--'}%`,
        actions: <span />,
      };

      setMembers([totalRow, ...mappedMembers]);
      setTotalPages(response.data?.pageInfo.totalPages ?? 1);
      setTotalCounts(response.data?.pageInfo.totalCounts ?? 0);
    } else {
      // Handle API errors
    }
    setIsLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(debounce((query) => setSearchQuery(query), 500), []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    history.push(`?page=${page}`);
  };

  const handlePageSizeChange = (pageSize) => {
    setCurrentPage(1);
    setTotalPages(Math.ceil(totalCounts / pageSize));
    setPageSize(pageSize);
  };

  useEffect(() => {
    handleSelectDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, sort, pageSize, date, selectedTeamId]);

  const handleSort = debounce((sortKey, sortOrder) => {
    setIsLoading(true);
    setSort(!sort);
    setSortKey(sortKey);
    setSortOrder(sortOrder);
  }, 250);

  const getTeams = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAllTeamInfo(activeWorkspace.id),
      timeout: 60000,
    });

    if (response.success) {
      setTeams(response.data.teams);
    }
  };

  useEffect(() => {
    getTeams();
  }, []);

  const handleSelectTimezone = async (timezone) => {
    setSelectedTimezone(timezone);
    await handleSelectDate(timezone);
  };

  return (
    <div>
      {showExportAllModal && (
      <ExportAllModal
        teams={teams}
        selectedTimezone={selectedTimezone}
        selectedTeamId={selectedTeamId}
        setSelectedTeamId={setSelectedTeamId}
        closeModal={() => setShowExportAllModal(false)}
      />
      )}
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header">
          <div className="screen-header-container d-flex justify-content-between position-relative">
            <div className="flex-row align-c w-100 justify-content-between">
              <h3
                className="header-manage-member"
                style={{ fontWeight: 600, width: 'auto' }}
                data-testid="title"
              >
                Timesheets
              </h3>
              <Button
                sx={{ marginLeft: 'auto' }}
                variant="outlined"
                onClick={() => {
                  setShowExportAllModal(true);
                }}
                data-testid="export-all-btn"
              >
                Export All
              </Button>
            </div>
          </div>
          <p
            className="main-sub-text"
            data-testid="subtitle"
          >
            View time spent working and activity
          </p>
          <div className="flex-row align-c justify-content-between" style={{ gap: 20, marginTop: 28 }}>
            <DateRangePicker
              value={date}
              onChange={(_date) => setDate(_date)}
              className="w-auto"
              placement="bottomStart"
            />
            <div className="d-flex justify-content-end gap-2">
              <div className="d-flex gap-2">
                <div className="searchBox">
                  <input
                    type="text"
                    placeholder="Search for user"
                    onChange={(e) => handleSearch(e.target.value)}
                    data-testid="search"
                  />
                  <i className="fa fa-search searchIcon" />
                </div>
                <TeamSelect
                  menuItems={teams}
                  value={selectedTeamId}
                  setSelectedTeamId={setSelectedTeamId}
                  handleForm={(e) => setSelectedTeamId(e.target.value)}
                />
              </div>
              <TimezoneSelectWrapper
                value={selectedTimezone}
                onChange={(e) => handleSelectTimezone(e.target.value, date)}
              />
            </div>
          </div>
          <CustomTable
            headers={TABLE_HEADERS}
            items={members}
            loading={isLoading}
            handleSort={handleSort}
            styles={CustomTableStyles}
          />
          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            totalCounts={totalCounts}
            onPageChange={handlePageChange}
            setPageSize={handlePageSizeChange}
            styles={{ margin: 0 }}
          />
        </div>
      </div>
    </div>
  );
}

Timesheets.propTypes = {

};

export default Timesheets;
